import { Alert } from "@/components/Alert"
import { Button } from "@/components/Button"
import { Input } from "@/components/Form/Input"
import { Selector } from "@/components/Selector"
import { ITableHead, Table } from "@/components/Table"
import { Title } from "@/components/Title"
import { isRainProvider } from "@/constants/ProviderConstants"
import { useMediaQuery } from "@/hooks/useMediaQuery"
import { useNavigate } from "@/hooks/useNavigate"
import { getCoinIcon } from "@/pages/Topup/sections/ByCrypto/CurrencyTable"
import { IOption } from "@/types"
import { DepositType, ECardType, PaymentSystem, useCurrencyTable, useTopUpStore } from "@usher/pe-client-front-end-lib"
import { Fragment, useEffect, useMemo, useState } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { useLocation } from "react-router-dom"

const Row = ({ title, value }: { title: string; value: string | number }) => {
  return (
    <div className="flex flex-col gap-2 mini:flex-row mini:justify-between xs:justify-start">
      <span className="font-[ChakraPetchBold] text-sm">{title}:</span>
      <div className="font-bold text-sm mini:text-right xs:text-left">{value}</div>
    </div>
  )
}
const TABLE_HEADS: ITableHead[] = [
  {
    key: "currency",
  },
  {
    key: "amount",
    label_id: "amount",
  },
  {
    key: "completion",
    label_id: "completion",
  },
  {
    key: "spend",
    label_id: "spend",
  },
  {
    key: "action",
  },
]
export const SelectPayment = ({
  depositType = DepositType.Card,
  isOrder,
}: {
  depositType?: DepositType
  isOrder?: boolean
  paymentSystem?: PaymentSystem
}) => {
  const { state } = useLocation()
  const navigate = useNavigate()
  const currentDate = new Date(Date.now())
  currentDate.setDate(currentDate.getDate() + 14)
  const { $t: t } = useIntl()
  const [value, setValue] = useState<IOption>()
  const isMobile = useMediaQuery("(max-width: 768px)")
  const { amounts, tableData } = useCurrencyTable(depositType, isOrder)
  const [filterValue, setFilterValue] = useState("")
  const { setCrypto, fiat, setDeposit, cardType, card } = useTopUpStore((state) => ({
    setCrypto: state.setCrypto,
    setFiat: state.setFiat,
    setDeposit: state.setDeposit,
    cardType: state.cardTypeId,
    fiat: state.fiat,
    card: state.card,
  }))
  const isRainProviderSelected = isRainProvider((state as { provider_id?: string })?.provider_id)
  const { allPrices } = useMemo(() => {
    const buffer = [...tableData]
    const trc20Index = buffer.findIndex((price) => price.symbol === "USDT-TRC20")
    const trc = buffer.splice(trc20Index, trc20Index > 0 ? 1 : 0)
    const allPrices = [...trc, ...buffer].filter(
      (price) => !["num", "bch", ...(!isRainProviderSelected ? ["usdc-base"] : [])].includes(price.symbol.toLowerCase()),
    )
    return { tableData: buffer, trc, allPrices }
  }, [tableData, isRainProviderSelected])

  const [filtered, setFiltered] = useState<typeof allPrices>(allPrices)
  useEffect(() => {
    if (filterValue.length > 0) setFiltered(allPrices.filter((price) => price.symbol.toLowerCase().startsWith(filterValue.toLowerCase())))
    else setFiltered(allPrices)
  }, [filterValue, allPrices])
  const selected = useMemo(() => filtered.find((data) => data.symbol === value?.value) ?? filtered[0], [value, filtered])
  const selectorOptions = useMemo(
    () =>
      filtered.map((opt) => ({
        label: opt.symbol,
        value: opt.symbol,
      })),
    [filtered],
  )
  const tableRows = useMemo(
    () => {
      let navigateUrl = "/cards/create/instructions"
      if (depositType === DepositType.IBAN) navigateUrl = "/iban/order/instructions"
      return filtered
        .filter((data) => data.symbol.toLowerCase() !== "ltc")
        .map((data) => {
          const isBTC = data.symbol.toLowerCase() === "btc"
          const fraction = isBTC ? 8 : 5
          const isBase = data.symbol.toLowerCase() === "usdc-base"
          return {
            currency: (
              <Fragment>
                <div className="flex items-center gap-2.5 text-base font-bold text-black">
                  <img className="w-5 h-5" src={`/client/img/coins/${getCoinIcon(data.symbol)}.svg`} alt="" />
                  {data.symbol}
                </div>
                {isBase && (
                  <div className="flex items-center gap-2.5 text-red-600 font-normal">
                    Please make sure this is the
                    <br /> BASE network, not Ethereum
                  </div>
                )}
              </Fragment>
            ),
            amount: (
              <span>
                {Number(data.amount).toFixed(2)} {fiat}
              </span>
            ),
            completion: t({ id: "from6" }),
            spend: (
              <>
                <span className="itemSpend">{Number(data.spend).toFixed(fraction)}</span> {data.symbol}
              </>
            ),
            action: (
              <div className="w-full text-right">
                <Button
                  className="inline-block"
                  onClick={() => {
                    if (amounts != null) {
                      setCrypto(data.symbol)
                      setDeposit(String(amounts.monthly_fee + amounts.order_fee))
                      // setFiat("EUR")
                      navigate(navigateUrl, { keepSearch: true, keepState: true })
                    }
                  }}
                >
                  <FormattedMessage id="pay" />
                </Button>
              </div>
            ),
          }
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtered, amounts, state],
  )
  return (
    <div>
      {amounts != null && (
        <Alert>
          <span>
            <FormattedMessage
              id={`${depositType === DepositType.Card ? "card-issue" : "iban-issue"}`}
              values={{
                amount: amounts.order_fee,
                fee: amounts.monthly_fee,
                total: amounts.order_fee + amounts.monthly_fee,
                fiat,
              }}
            />
          </span>
        </Alert>
      )}
      {cardType === String(ECardType.Physical) && (
        <Alert className="mt-[20px]">
          <span>
            <FormattedMessage
              id="attention"
              values={{
                date: `${String(currentDate.getDate()).padStart(2, "0")}/${String(currentDate.getMonth() + 1).padStart(
                  2,
                  "0",
                )}/${currentDate.getFullYear()}`,
                b: (chunk) => <strong>{chunk}</strong>,
              }}
            />
          </span>
        </Alert>
      )}
      <div className="currency-table">
        <div className="currency-table__titlebar">
          <Title variant="title">
            <FormattedMessage id="select-payment" />
          </Title>
          {isMobile ? (
            <Selector
              leftSection={selected != null && <img className="w-5 h-5" src={`/client/img/coins/${getCoinIcon(selected.symbol)}.svg`} alt="" />}
              value={value ?? selectorOptions[0] ?? null}
              onChange={setValue}
              options={selectorOptions}
              renderOptions={(option, props) => (
                <div {...props}>
                  <img className="w-5 h-5 mr-2" src={`/client/img/coins/${getCoinIcon(option.value)}.svg`} alt="" />
                  {option.label}
                </div>
              )}
              variant="contained"
              classNames={{ control: "w-full" }}
              searchable
            />
          ) : (
            <Input placeholder={t({ id: "filter" })} onChange={(e) => setFilterValue(e.currentTarget.value)} />
          )}
          {value?.value?.toLowerCase() === "usdc-base" && isMobile && (
            <p className="text-red-600 text-sm">Please make sure this is the BASE network, not Ethereum</p>
          )}
        </div>
        {isMobile ? (
          <>
            {selected != null && (
              <div className="px-2 flex flex-col gap-2.5">
                <Row title={t({ id: "amount" })} value={`${Number(selected?.amount).toFixed(2)} EUR`} />
                {/* <Row title="Fee" value={`${(parseFloat(selected?.spend ?? "0") * ((selected?.fee ?? 100) / 100)).toFixed(5)} ${selected?.symbol}`} /> */}
                <Row title="Completion" value="from 6 Blockchain Network Confirmations" />
                <Row title="You spend" value={`${selected?.spend} ${selected?.symbol}`} />
              </div>
            )}
            <Button
              className="w-full mt-4"
              onClick={() => {
                if (selected == null) return
                if (amounts != null) {
                  setCrypto(selected.symbol)
                  setDeposit(String(amounts.monthly_fee + amounts.order_fee))
                  navigate("/cards/create/instructions", { keepSearch: true, keepState: true })
                }
              }}
            >
              <FormattedMessage id="pay" />
            </Button>
          </>
        ) : (
          <Table
            heads={TABLE_HEADS}
            data={tableRows}
            classNames={{ header: "currency-table-head", row: "font-bold !text-[#777e8a] currency-table-row" }}
          />
        )}
      </div>
    </div>
  )
}
